import { Dispatch, MutableRefObject, SetStateAction, useEffect, useState } from 'react';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import settingsSiteNames from '../../businessLogicService/settingsSiteNames';
import { SitesQuery$data } from '../../graphQLOperations/__generated__/SitesQuery.graphql';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { sitesTimezoneQuery, sitesUpdateSites } from '../../graphQLOperations/Sites';
import { SitesTimezoneQuery } from '../../graphQLOperations/__generated__/SitesTimezoneQuery.graphql';
import { SitesUpdateSiteMutation } from '../../graphQLOperations/__generated__/SitesUpdateSiteMutation.graphql';
import { Toast } from 'primereact/toast';

interface Props {
  data: SitesQuery$data | null;
  toast: MutableRefObject<Toast | null>;
  setFetchKey: Dispatch<SetStateAction<number>>;
  setRefetchingData: Dispatch<SetStateAction<boolean>>;
}
interface Timezone {
  id: string;
  name: string;
}
const SettingsTabTimezone = ({ data, toast, setFetchKey, setRefetchingData }: Props) => {
  const [checked, setChecked] = useState<boolean>(false);
  const [timezone, setTimezone] = useState<Timezone>();
  const [timezoneMap, setTimezoneMap] = useState<
    {
      siteId: string;
      timezone?: Timezone;
    }[]
  >([]);
  const [timezones, setTimezones] = useState<Timezone[]>([]);
  const [filteredTimezones, setFilteredTimezones] = useState<Timezone[]>([]);
  const timezoneData = useLazyLoadQuery<SitesTimezoneQuery>(sitesTimezoneQuery, {});
  const [updateSites, loading] = useMutation<SitesUpdateSiteMutation>(sitesUpdateSites);

  const sitesTemplate = timezoneData?.allTimeZones?.map(element => {
    return element;
  });

  const searchTimezone = (event: AutoCompleteCompleteEvent) => {
    let _filteredTimezone;
    if (!event?.query?.trim().length) {
      _filteredTimezone = [...timezones];
    } else {
      _filteredTimezone = timezones.filter(timezone => {
        return timezone.name.toLowerCase().includes(event.query.toLowerCase());
      });
    }
    setFilteredTimezones(_filteredTimezone);
  };

  const isDisable = () => {
    const validation = !timezoneMap?.every(t => t?.timezone?.id === timezoneMap?.[0]?.timezone?.id);
    if (!data?.sites?.nodes?.length) return false;
    return validation;
  };

  const onSubmit = () => {
    updateSites({
      variables: {
        input: checked
          ? data?.sites?.nodes?.map(site => ({
              id: site?.id ?? '',
              timeZoneId: timezone?.id ?? '',
            })) ?? []
          : timezoneMap?.map(t => ({
              id: t?.siteId ?? '',
              timeZoneId: t?.timezone?.id ?? '',
            })) ?? [],
      },
      onCompleted: () => {
        setFetchKey(new Date().getTime());
        setRefetchingData(true);
        toast?.current?.show({
          severity: 'success',
          summary: 'Time Zones Updated',
          detail: 'The Time Zones available to your site(s) have been updated',
        });
      },
      onError: () =>
        toast?.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Something went wrong. Please try again',
        }),
    });
  };

  useEffect(() => {
    setTimezones(sitesTemplate as Timezone[]);
  }, []);

  useEffect(() => {
    const timeData = data?.sites?.nodes?.map(time => time);
    if (timeData) {
      setTimezone({
        name: timeData?.[0]?.timeZone?.name ?? '',
        id: timeData?.[0]?.timeZone?.id ?? '',
      });
      setTimezoneMap(
        timeData?.map(t => ({
          siteId: t?.id ?? '',
          timezone: {
            id: t?.timeZone?.id ?? '',
            name: t?.timeZone?.name ?? '',
          },
        })),
      );
    }
  }, [data]);

  return (
    <>
      <div className="flex flex-column nc_content-frame" id="timezoneSettings">
        <div className="flex align-items-center">
          <Checkbox
            id="timezones-allSites"
            inputId="timezones-allSites"
            checked={checked}
            onChange={e => {
              const tempChecked = e.checked!;
              setChecked(tempChecked);
              if (!tempChecked) {
                setTimezoneMap(timezoneMap?.map(prev => ({ ...prev, timezone })));
              } else {
                setTimezone(timezoneMap[0].timezone);
              }
            }}
            disabled={isDisable()}
          />
          <label htmlFor="timezones-allSites">Use the same selection of time zones for all sites</label>
        </div>
        {checked && (
          <div className="flex flex-column gap-md">
            <h1>{settingsSiteNames(data?.sites?.nodes?.map(site => site?.name ?? '') ?? [])}</h1>
            <div className="p-inputgroup w-12 lg:w-6">
              <span className="p-inputgroup-addon">Time Zone</span>
              <AutoComplete
                suggestions={filteredTimezones}
                completeMethod={searchTimezone}
                value={timezone}
                dropdown
                forceSelection
                field="name"
                onChange={e => {
                  setTimezone(e?.value);
                }}
              />
            </div>
          </div>
        )}
        {!checked &&
          data?.sites?.nodes?.map((site, key) => (
            <div key={key} className="flex flex-column gap-md">
              <h1>{site?.name}</h1>
              <div className="p-inputgroup w-12 lg:w-6">
                <span className="p-inputgroup-addon">Time Zone</span>
                <AutoComplete
                  suggestions={filteredTimezones}
                  completeMethod={searchTimezone}
                  value={timezoneMap?.find(t => t?.siteId === site?.id)?.timezone}
                  dropdown
                  forceSelection
                  field="name"
                  onChange={e => {
                    if (timezoneMap?.some(t => t?.siteId === site?.id)) {
                      setTimezoneMap(
                        timezoneMap?.map(t => {
                          if (t?.siteId === site?.id) {
                            return {
                              ...t,
                              timezone: e?.value,
                            };
                          }
                          return t;
                        }),
                      );
                    } else {
                      setTimezoneMap(prev => [...prev, { siteId: site?.id ?? '', timezone: e?.value }]);
                    }
                  }}
                />
              </div>
            </div>
          ))}
        <div>
          <Button
            loading={loading}
            disabled={loading || !timezone}
            onClick={onSubmit}
            label="Save"
            icon="nc_icon-nc_save icon-sm"
            aria-label="Save settings"
            rounded
            id="timezonesSaveButton"
          />
        </div>
      </div>
    </>
  );
};

export default SettingsTabTimezone;
